import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Play, RefreshCw, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import { useT } from "@/lib/i18n";

const SCENARIO_KEYS = ["scheduled_job", "duplicate_charge", "api_timeout"];

export default function Demo() {
  const t = useT();
  const [scenario, setScenario] = useState("scheduled_job");
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const start = async () => {
    clearInterval(pollRef.current);
    setRun(null);
    const { data } = await api.post("/demo/start", { scenario });
    setRunId(data.run_id);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/demo/${data.run_id}`);
        setRun(r.data);
        if (r.data.status === "complete") clearInterval(pollRef.current);
      } catch {}
    }, 700);
  };

  const reset = () => { clearInterval(pollRef.current); setRunId(null); setRun(null); };

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="overline mb-4">{t("demo.overline")}</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
          {t("demo.h1.1")} <span className="text-primary">{t("demo.h1.2")}</span>.
        </h1>
        <p className="mt-4 text-white/60 max-w-2xl">{t("demo.subtitle")}</p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
          {SCENARIO_KEYS.map((key) => (
            <button
              key={key}
              onClick={() => setScenario(key)}
              data-testid={`demo-scenario-${key}`}
              disabled={!!runId && run && run.status !== "complete"}
              className={`text-left surface p-5 transition ${scenario === key ? "border-primary ring-1 ring-primary/40" : "hover:border-white/20"} disabled:opacity-50`}
            >
              <div className="overline">{key}</div>
              <div className="font-display font-bold text-lg mt-1">{t(`demo.scenario.${key}.label`)}</div>
              <div className="text-sm text-white/50 mt-1">{t(`demo.scenario.${key}.desc`)}</div>
            </button>
          ))}
        </div>

        <div className="mt-6 flex gap-3">
          {!runId && (
            <button onClick={start} className="btn-primary" data-testid="demo-start-btn">
              <Play className="w-4 h-4" /> {t("demo.run")}
            </button>
          )}
          {runId && run?.status === "complete" && (
            <button onClick={reset} className="btn-ghost" data-testid="demo-reset-btn">
              <RefreshCw className="w-4 h-4" /> {t("demo.tryAnother")}
            </button>
          )}
        </div>

        {runId && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 surface p-6">
              <div className="overline mb-4">{t("demo.trace")}</div>
              {run?.card && (
                <div className="mb-4 pb-4 border-b border-white/10">
                  <div className="text-xs font-mono text-white/40">{run.card.tenant_id} · {run.card.order_id}</div>
                  <div className="text-white/90 mt-1 text-sm">{run.card.title}</div>
                </div>
              )}
              <div className="space-y-2">
                {run?.steps_completed?.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm" data-testid={`demo-step-${s.key}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    <span className="text-white/80">{s.label}</span>
                  </div>
                ))}
                {run?.status === "analyzing" && (
                  <div className="flex items-center gap-3 text-sm text-white/60" data-testid="demo-step-loading">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span>{t("demo.working")}<span className="animate-pulse">...</span></span>
                  </div>
                )}
              </div>
              <div className="mt-6">
                <div className="flex justify-between font-mono text-xs text-white/40 mb-2">
                  <span>{t("demo.progress")}</span>
                  <span>{run?.progress ?? 0}%</span>
                </div>
                <div className="h-1 bg-white/10">
                  <div className="h-full bg-primary transition-all" style={{ width: `${run?.progress ?? 0}%` }} />
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 surface p-6">
              <div className="overline mb-4">{t("demo.report")}</div>
              {!run?.report && (
                <div className="text-white/40 text-sm">{t("demo.report.empty")}</div>
              )}
              {run?.report && (
                <div className="space-y-6" data-testid="demo-report">
                  <div>
                    <div className="overline text-amber-400 mb-2">{t("demo.report.hypotheses")}</div>
                    <ul className="space-y-1.5 text-sm text-white/80 list-disc list-inside">
                      {run.report.hypotheses.map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="overline text-blue-400 mb-2">{t("demo.report.facts")}</div>
                    <ul className="space-y-1.5 text-sm text-white/80 list-disc list-inside font-mono text-xs">
                      {run.report.facts.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                  <div className="border-l-2 border-emerald-400 pl-4">
                    <div className="overline text-emerald-400 mb-2">{t("demo.report.rootCause")}</div>
                    <p className="text-sm text-white/90">{run.report.root_cause}</p>
                  </div>
                  <div className="border-l-2 border-primary pl-4">
                    <div className="overline text-primary mb-2">{t("demo.report.resolution")}</div>
                    <p className="text-sm text-white/90">{run.report.resolution}</p>
                  </div>
                  <div className="flex justify-between border-t border-white/10 pt-4 font-mono text-xs text-white/40">
                    <span>model · {run.report.ai_model}</span>
                    <span>⏱ {t("demo.report.saved")} ~{run.report.time_saved_minutes} min</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-16 surface p-8 text-center">
          <div className="overline mb-3">{t("demo.cta.overline")}</div>
          <h3 className="font-display font-black text-2xl tracking-tighter">{t("demo.cta.title")}</h3>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/signup" className="btn-primary" data-testid="demo-cta-signup">{t("demo.cta.create")} <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/pricing" className="btn-ghost">{t("demo.cta.pricing")}</Link>
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
